import { Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MenuItem } from '@card-software/library';
import { MasterDataService, RolesBootstrapService, Site, SiteBootstrapService, SiteService } from '@card-software/nexus-utilities';
import { UserAuthenticationService } from './services/user-authentication.service';
import { EnumService } from './services/enum.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { MessageService } from 'primeng/api';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import _ from 'lodash';
import {
  AccountsPayableRoles,
  BankAccountRoles,
  BusinessRoles,
  CostingRoles,
  ExchangeRateRoles,
  CostCenterRoles,
  ExpenseConceptRoles,
  GlAccountRuleRoles,
  PettyCashRoles,
} from './models/roles/roles-group';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  title = 'finance-ui';
  isLoginPage = false;

  readonly environment = environment.environment;

  menuItems: MenuItem[] = [];

  constructor(
    private router: Router,
    private userAuthenticationService: UserAuthenticationService,
    private enumService: EnumService,
    private translate: TranslateService,
    private messageService: MessageService,
    private siteBootstrapService: SiteBootstrapService,
    private rolesBootstrapService: RolesBootstrapService,
  ) {
    this.translate.setDefaultLang('en');
    const savedLang = localStorage.getItem('lang');
    const browserLang = this.translate.getBrowserLang();
    const selectedLang = savedLang ?? (browserLang === 'es' ? 'es' : 'en');
    this.translate.use(selectedLang);
    GlobalMessageUtil.init(this.messageService);

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isLoginPage = _.includes(event.url, '/login');
      }
    });

    const token = this.userAuthenticationService.getStoredJwtToken();
    if (token) {
      MasterDataService.initialize(environment.proxyUrls.itemApi, token);
      this.rolesBootstrapService.initializeRoles(token);
      this.siteBootstrapService.initializeSites(token);
      if (!this.enumService.enumsReady()) {
        this.enumService.loadEnums().subscribe();
      }
    }

    this.translate.onLangChange.subscribe(() => {
      this.loadMenuItems();
    });

    this.loadMenuItems();
  }

  loadMenuItems(): void {
    this.menuItems = [
      {
        displayName: this.translate.instant('Exchange Rate'),
        routerLink: '/exchange-rate',
        redirect: true,
        roles: [ExchangeRateRoles.ExchangeRateViewer, ExchangeRateRoles.ExchangeRateAdmin],
      },
      {
        displayName: this.translate.instant('Costs'),
        routerLink: '/costs',
        redirect: false,
        children: [
          {
            displayName: this.translate.instant('Cost Analysis'),
            routerLink: '/cost-analysis',
            redirect: true,
          },
          {
            displayName: this.translate.instant('Pools'),
            routerLink: '/costs/pools',
            redirect: true,
            roles: [CostingRoles.FinanceCalculatorAdmin],
          },
          {
            displayName: this.translate.instant('Reports'),
            routerLink: '/costs/reports',
            redirect: true,
            roles: [CostingRoles.FinanceCostingAdmin],
          },
        ],
      },
      {
        displayName: this.translate.instant('Monthly Uploads'),
        routerLink: '/monthly-uploads',
        redirect: true,
      },
      {
        displayName: this.translate.instant('Business'),
        routerLink: '/business',
        redirect: false,
        roles: [BusinessRoles.BusinessViewer, BusinessRoles.BusinessAdmin],
        children: [
          {
            displayName: this.translate.instant('Businesses'),
            routerLink: '/businesses',
            redirect: true,
            roles: [BusinessRoles.BusinessViewer, BusinessRoles.BusinessAdmin],
          },
          {
            displayName: this.translate.instant('Tax Authorizations'),
            routerLink: '/tax-authorizations',
            redirect: true,
            roles: [BusinessRoles.BusinessViewer, BusinessRoles.BusinessAdmin],
          },
        ],
      },
      {
        // Petty cash and trips (#5778). The group shows for anyone who can open at least one child; each page
        // (funds, trips, receipts, claims) adds its own child and roles here as it ships.
        displayName: this.translate.instant('Petty Cash & Trips'),
        routerLink: '/petty-cash',
        redirect: false,
        roles: [
          BusinessRoles.BusinessViewer,
          BusinessRoles.BusinessAdmin,
          PettyCashRoles.PettyCashFundViewer,
          PettyCashRoles.PettyCashFundAdmin,
          PettyCashRoles.PettyCashTreasurer,
          PettyCashRoles.TripViewer,
          PettyCashRoles.TripAdmin,
          PettyCashRoles.TripApprover,
          PettyCashRoles.ExpenseClaimViewer,
          PettyCashRoles.ExpenseClaimAdmin,
          PettyCashRoles.ClaimApprover,
          PettyCashRoles.ClaimAccountingReviewer,
        ],
        children: [
          {
            displayName: this.translate.instant('PettyCashFund_Title'),
            routerLink: '/petty-cash-funds',
            redirect: true,
            roles: [PettyCashRoles.PettyCashFundViewer, PettyCashRoles.PettyCashFundAdmin, PettyCashRoles.PettyCashTreasurer],
          },
          {
            displayName: this.translate.instant('Trip_Title'),
            routerLink: '/trips',
            redirect: true,
            roles: [PettyCashRoles.TripViewer, PettyCashRoles.TripAdmin, PettyCashRoles.TripApprover],
          },
          {
            displayName: this.translate.instant('Receipt_Title'),
            routerLink: '/expense-receipts',
            redirect: true,
            roles: [
              PettyCashRoles.PettyCashFundViewer,
              PettyCashRoles.PettyCashFundAdmin,
              PettyCashRoles.TripViewer,
              PettyCashRoles.TripAdmin,
              PettyCashRoles.TripApprover,
              PettyCashRoles.ExpenseClaimViewer,
              PettyCashRoles.ExpenseClaimAdmin,
            ],
          },
          {
            displayName: this.translate.instant('ExpenseClaim_Title'),
            routerLink: '/expense-claims',
            redirect: true,
            roles: [
              PettyCashRoles.PettyCashFundViewer,
              PettyCashRoles.PettyCashFundAdmin,
              PettyCashRoles.TripViewer,
              PettyCashRoles.TripAdmin,
              PettyCashRoles.TripApprover,
              PettyCashRoles.ExpenseClaimViewer,
              PettyCashRoles.ExpenseClaimAdmin,
              PettyCashRoles.ClaimApprover,
              PettyCashRoles.ClaimAccountingReviewer,
            ],
          },
          {
            displayName: this.translate.instant('EmployeeAdvance_Title'),
            routerLink: '/employee-advances',
            redirect: true,
            roles: [PettyCashRoles.PettyCashTreasurer, PettyCashRoles.TripViewer, PettyCashRoles.TripAdmin],
          },
          {
            displayName: this.translate.instant('ExpenseSettlement_Title'),
            routerLink: '/expense-settlements',
            redirect: true,
            roles: [
              PettyCashRoles.PettyCashTreasurer,
              PettyCashRoles.PettyCashFundViewer,
              PettyCashRoles.PettyCashFundAdmin,
              PettyCashRoles.TripViewer,
              PettyCashRoles.TripAdmin,
              PettyCashRoles.ExpenseClaimViewer,
              PettyCashRoles.ExpenseClaimAdmin,
            ],
          },
          {
            displayName: this.translate.instant('EmployeeAccount_Title'),
            routerLink: '/employees',
            redirect: true,
            roles: [BusinessRoles.BusinessViewer, BusinessRoles.BusinessAdmin],
          },
        ],
      },
      {
        displayName: this.translate.instant('General Accounting'),
        routerLink: '/general-accounting',
        redirect: false,
        // The group shows for anyone who can open at least one child; each child carries its own roles.
        roles: [
          AccountsPayableRoles.AccountsPayableAdmin,
          CostCenterRoles.CostCenterViewer,
          ExpenseConceptRoles.ExpenseConceptViewer,
          ExpenseConceptRoles.ExpenseConceptAdmin,
          GlAccountRuleRoles.GlAccountRuleViewer,
          GlAccountRuleRoles.GlAccountRuleAdmin,
        ],
        children: [
          {
            displayName: this.translate.instant('Chart of Accounts'),
            routerLink: '/chart-of-accounts',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin],
          },
          {
            displayName: this.translate.instant('Journal Entries'),
            routerLink: '/journal-entries',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin],
          },
          {
            displayName: this.translate.instant('Accounting Periods'),
            routerLink: '/accounting-periods',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin],
          },
          {
            displayName: this.translate.instant('Cost Centers'),
            routerLink: '/cost-centers',
            redirect: true,
            roles: [CostCenterRoles.CostCenterViewer],
          },
          {
            displayName: this.translate.instant('Expense Concepts'),
            routerLink: '/expense-concepts',
            redirect: true,
            roles: [ExpenseConceptRoles.ExpenseConceptViewer, ExpenseConceptRoles.ExpenseConceptAdmin],
          },
          {
            displayName: this.translate.instant('GL Account Rules'),
            routerLink: '/gl-account-rules',
            redirect: true,
            roles: [GlAccountRuleRoles.GlAccountRuleViewer, GlAccountRuleRoles.GlAccountRuleAdmin],
          },
        ],
      },
      {
        displayName: this.translate.instant('Banks'),
        routerLink: '/banks',
        redirect: false,
        children: [
          {
            displayName: this.translate.instant('Bank Accounts'),
            routerLink: '/bank-accounts',
            redirect: true,
            roles: [BankAccountRoles.BankAccountViewer, BankAccountRoles.BankAccountAdmin],
          },
        ],
      },
      {
        displayName: this.translate.instant('Accounts Payable'),
        routerLink: '/accounts-payable',
        redirect: false,
        roles: [AccountsPayableRoles.AccountsPayableAdmin],
        children: [
          {
            displayName: this.translate.instant('Documents'),
            routerLink: '/ap-documents',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin],
          },
          {
            displayName: this.translate.instant('AP Aging'),
            routerLink: '/ap-aging',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin],
          },
          {
            displayName: this.translate.instant('Cash Requests'),
            routerLink: '/cash-requests',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin],
          },
          {
            displayName: this.translate.instant('Payment Requests'),
            routerLink: '/ap-payment-requests',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin, AccountsPayableRoles.AccountsPayableApprover],
          },
          {
            displayName: this.translate.instant('Payments'),
            routerLink: '/ap-payments',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin],
          },
          {
            displayName: this.translate.instant('PO Settlement'),
            routerLink: '/po-settlement',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin],
          },
          {
            displayName: this.translate.instant('Advance Requests'),
            routerLink: '/ap-advance-requests',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin, AccountsPayableRoles.AccountsPayableApprover],
          },
          {
            displayName: this.translate.instant('Vendor Advances'),
            routerLink: '/ap-advances',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin],
          },
        ],
      },
      {
        displayName: this.translate.instant('Tax & Withholding'),
        routerLink: '/tax-withholding',
        redirect: false,
        roles: [AccountsPayableRoles.AccountsPayableAdmin],
        children: [
          {
            displayName: this.translate.instant('Withholding Certificates'),
            routerLink: '/ap-withholdings',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin],
          },
          {
            displayName: this.translate.instant('Withholding Remittance'),
            routerLink: '/withholding-remittance',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin],
          },
          {
            displayName: this.translate.instant('Withholding Taxes'),
            routerLink: '/withholding-taxes',
            redirect: true,
            roles: [AccountsPayableRoles.AccountsPayableAdmin],
          },
        ],
      },
    ];
  }

  get isLoggedIn() {
    return this.userAuthenticationService.isUserAuthenticated;
  }

  get userLite() {
    return this.userAuthenticationService.userLite;
  }

  get userToken() {
    return this.userAuthenticationService.getStoredJwtToken() || '';
  }

  get showSidebar(): boolean {
    const isLoginPage = this.router.url.includes('/login');
    return this.userAuthenticationService.isUserAuthenticated && !isLoginPage;
  }

  get sites(): Site[] {
    return SiteService.getSites();
  }

  get selectedSiteId(): string {
    return SiteService.getSelectedSiteId() || '';
  }

  onSiteChanged(siteId: string) {
    SiteService.setSelectedSite(siteId);
  }

  /** Opens the Info guide (#4894). Reachable from every screen, so it lives here rather than on a page. */
  goToInfo() {
    this.router.navigate(['/info']);
  }

  onLogout() {
    this.userAuthenticationService.logout();
  }
}
